// VDB-backed hooks for the SPA. Mirrors the JSON shapes from
// crates/tres-api/src/handlers/vdb.rs.

import { useQuery } from '@tanstack/react-query';
import { apiFetch, type ApiResult } from '@/lib/api';
import { useApiSession } from '@/lib/useApiSession';

export interface VdbSearchResult {
  uri: string;
  entity_type: string;
  label: string | null;
  score: number;
  graph: string | null;
  /** Distinguishing classes (domain types) — lets the UI tell apart
   *  same-name entities, e.g. "Iron Man" the Mantle vs the
   *  CostumedIdentity. */
  classes: { uri: string; label: string }[];
  /** Permanent palette slot (0–11) for the entity's base-class family — the
   *  VDB's node colour authority. Absent when the entity has no top-class
   *  ancestor. */
  color_index?: number;
  /** Distinct tag names present on the entity's statements (QROS only;
   *  absent when none or on RDF backends). Names only — a quiet signal that
   *  tagged facts live behind this result. */
  tag_names?: string[];
}

export interface VdbEdge {
  edge_id: string;
  source_id: string;
  target_id: string;
  /** Graph-independent identity — key viz nodes by these, not the
   *  per-graph source_id/target_id, so expand targets merge with the
   *  URI-keyed class nodes instead of duplicating. */
  source_uri: string;
  target_uri: string;
  label: string;
  predicate_uri: string;
  classification: string;
  should_visualize: boolean;
  score: number;
  graph: string | null;
  /** Base-class palette slots for the edge's endpoints, so an expand colours
   *  newly-revealed nodes the same as the rest of their family. */
  source_color_index?: number;
  target_color_index?: number;
  /** Display labels for the endpoints — expand-revealed nodes render these,
   *  falling back to the local name only when the entity has no label. */
  source_label?: string | null;
  target_label?: string | null;
}

interface SearchResponse {
  results: VdbSearchResult[];
}

interface EdgesResponse {
  edges: VdbEdge[];
}

/** What an index that is not current waits on: a clear queued or running, a
 *  load writing, the quiet window after the last write, or a batch to merge. */
export type IndexWait =
  | { on: 'clear' }
  | { on: 'load' }
  | { on: 'settle'; seconds: number }
  | { on: 'batch' };

export type VdbStatus =
  | { state: 'pending' }
  | {
      state: 'building';
      started_at: string;
      /** The previous index keeps answering while this build runs — true for a
       *  merge and for a full build until it clears the store. False only in a
       *  rebuild's index phase, the one window with nothing to answer from. */
      serving: boolean;
      serving_entities?: number;
      /** Where the embedder is, once a long pass has a rate. */
      progress?: { done: number; total: number; rate_per_s: number; eta_s: number | null };
      /** The index was released to make room for another database and is
       *  coming back from its saved copy. */
      reloading?: boolean;
    }
  | {
      state: 'ready';
      last_built: string;
      duration_ms: number;
      entities: number;
      relationships: number;
      triples: number;
      graphs: number;
      entity_ceiling: number;
      /** What the index waits on while changes are written but not yet in
       *  it; the counts are the index's until it rebuilds, not the store's.
       *  Absent when the index is current. */
      waiting?: IndexWait;
      /** The derived indices the assistant reads, built after the index: when
       *  they finished and what each cost. Absent until warmed. */
      warmed?: {
        at: string;
        schema_classes: number;
        schema_properties: number;
        summary_ms: number;
        profile_ms: number;
        resolver_terms: number | null;
        resolver_ms: number;
      } | null;
    }
  | {
      /** A clear or delete stood the build down; the index rebuilds once the
       *  changes settle, and the prior index answers until then. */
      state: 'paused';
      since: string;
      /** `out_of_focus`: another database on the site is in use, and this
       *  index builds when this database is next used. */
      reason: 'settle' | 'out_of_focus';
      serving_entities?: number;
      waiting?: IndexWait;
    }
  | {
      /** The index was released to make room for another database; it
       *  reloads from its saved copy now that this database is in use. */
      state: 'released';
      since: string;
      released_entities?: number;
    }
  | { state: 'failed'; failed_at: string; error: string }
  | {
      /** A full build was refused — no room for it, or builds keep killing the
       *  process — and is asked for again on the next write. The index that
       *  was ready before keeps serving. */
      state: 'deferred';
      since: string;
      reason: 'memory' | 'breaker';
      rss_mb?: number;
      limit_mb?: number;
      needed_mb?: number;
      attempts?: number;
      serving_entities?: number;
    };

/** One line an operator can act on. */
export function deferredDetail(s: Extract<VdbStatus, { state: 'deferred' }>): string {
  if (s.reason === 'memory') {
    return `no room for a rebuild: ${s.rss_mb?.toLocaleString() ?? '?'} MB resident of ${
      s.limit_mb?.toLocaleString() ?? '?'
    } MB, ${s.needed_mb?.toLocaleString() ?? '?'} MB needed`;
  }
  return `${s.attempts ?? '?'} rebuilds did not complete; automatic rebuilds are stopped`;
}

/** Ask the server to rebuild the in-memory index. 409 when a build is already
 *  in flight — a refusal the caller must show, not swallow.
 *
 *  **L3 SUPPORT ONLY — deliberately not wired to any control (2026-08-26).**
 *  A rebuild can run for hours; the previous index answers through its
 *  fingerprint and extract passes, then search, viz and grounding answer 503
 *  from the clear until the new index is complete (20 minutes at a million
 *  statements), and it cannot be cancelled. It is kept here so support tooling
 *  can call it, not so a page can. */
export async function rebuildVdb(token: string): Promise<ApiResult<{ accepted: boolean }>> {
  return apiFetch('/api/v1/vdb/rebuild', token, { method: 'POST' });
}

/**
 * Polls /api/v1/vdb/status. Faster cadence while not ready, slow once
 * ready. Returns the last-known status while a refetch is in flight.
 */
export function useVdbStatus() {
  const { token, ready } = useApiSession();

  return useQuery<VdbStatus>({
    queryKey: ['vdb', 'status', token ?? 'anon'],
    enabled: ready,
    queryFn: async () => {
      const r = await apiFetch<VdbStatus>('/api/v1/vdb/status', token);
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
    refetchInterval: (query) => {
      const data = query.state.data;
      // Build is in flight or hasn't started — poll every 5s.
      if (!data || data.state !== 'ready' || data.waiting) return 5_000;
      // Steady-state — back off to once a minute.
      return 60_000;
    },
    // Show last-known status while the next refetch is running.
    placeholderData: (prev) => prev,
  });
}

/**
 * Type-ahead search against /api/v1/vdb/search. Triggers on >= 3 chars
 * (matches the desktop's behavior). Debouncing happens in the consumer
 * — this hook is plain TanStack Query so the caller controls when the
 * key changes.
 */
export type VdbSearchKind = 'class' | 'property' | 'instance';

/** Search matches LABELS, but addresses are first-class in this UI — they are
 *  shown on every resource, so people paste and type them. `:mat` is not the
 *  label `mat`, so a bare address found nothing and the surface said "no
 *  matches" for a thing that plainly exists.
 *
 *  Strip a leading prefix marker (`:mat`, `qros:mat`) before searching. Applied
 *  HERE rather than per call site so every surface behaves the same — search,
 *  the editor pickers, and viz all reach the VDB through this hook. */
export function searchTermFromInput(raw: string): string {
  return raw
    .trim()
    .replace(/^[A-Za-z][\w-]*:/, '')
    .replace(/^:/, '');
}

export function useVdbSearch(
  query: string,
  limit = 20,
  excludeProperties = false,
  kind?: VdbSearchKind,
) {
  const { token, ready } = useApiSession();
  const trimmed = searchTermFromInput(query);
  const enabled = ready && trimmed.length >= 3;
  // A search answered while the index was taking in a change is asked again
  // when that change lands, so a term just created is offered without a reload.
  const status = useVdbStatus().data;
  const indexVersion = status?.state === 'ready' ? `${status.last_built}:${status.waiting ? 'w' : ''}` : status?.state;

  return useQuery<VdbSearchResult[]>({
    queryKey: ['vdb', 'search', trimmed, limit, excludeProperties, kind ?? 'all', token ?? 'anon', indexVersion],
    enabled,
    staleTime: 30_000,
    queryFn: async () => {
      // Viz passes excludeProperties: a property is an edge, never a placeable node.
      // Editor pickers pass kind so a Class field only offers classes and a
      // predicate field only offers properties (VDB classification decides).
      const url =
        `/api/v1/vdb/search?q=${encodeURIComponent(trimmed)}&limit=${limit}` +
        (excludeProperties ? '&exclude_properties=true' : '') +
        (kind ? `&kind=${kind}` : '');
      const r = await apiFetch<SearchResponse>(url, token);
      if (!r.ok) throw new Error(r.message);
      return r.data.results;
    },
  });
}

export interface VdbTopClass {
  uri: string;
  label: string;
  node_id: string;
  subclass_count: number;
  importance: number;
  is_top_level: boolean;
  /** Family color slot (VDB class color map, inherited from the top-class
   *  ancestor). Null when the class has no ancestor family. */
  color_index: number | null;
  /** Hierarchy depth (0 = top class); shades the family color lighter with depth. */
  hierarchy_depth: number;
}

interface TopClassesResponse {
  classes: VdbTopClass[];
}

interface ClassHierarchyResponse {
  edges: VdbEdge[];
}

/**
 * Every subClassOf edge between classes in the active ontology, in one
 * round-trip. Pairs with `fetchVdbTopClasses` to populate the viz
 * canvas with the class tree at "Load Ontology" time.
 */
export async function fetchVdbClassHierarchy(
  token: string | undefined,
  graph: string | null,
): Promise<VdbEdge[]> {
  const params = new URLSearchParams();
  if (graph) params.set('graph', graph);
  const url = `/api/v1/vdb/class-hierarchy${params.toString() ? `?${params.toString()}` : ''}`;
  const r = await apiFetch<ClassHierarchyResponse>(url, token);
  if (!r.ok) throw new Error(r.message);
  return r.data.edges;
}

/**
 * Every class→class ObjectProperty edge implied by domain/range — the schema
 * relationships that connect the ontology beyond subClassOf. Drawn alongside
 * the hierarchy at "Load Ontology" time so the class graph is connected, not a
 * bare tree. Same wire shape as `fetchVdbClassHierarchy`.
 */
export async function fetchVdbSchemaEdges(token: string | undefined): Promise<VdbEdge[]> {
  const r = await apiFetch<ClassHierarchyResponse>('/api/v1/vdb/schema-edges', token);
  if (!r.ok) throw new Error(r.message);
  return r.data.edges;
}

/**
 * Top-level classes from the active ontology — what the desktop's
 * "Load Ontology" button surfaces. Each result is a starting point
 * for graph exploration; the desktop places these on the canvas as
 * the user's first nodes.
 */
export async function fetchVdbTopClasses(
  token: string | undefined,
  graph: string | null,
): Promise<VdbTopClass[]> {
  const params = new URLSearchParams();
  if (graph) params.set('graph', graph);
  const url = `/api/v1/vdb/top-classes${params.toString() ? `?${params.toString()}` : ''}`;
  const r = await apiFetch<TopClassesResponse>(url, token);
  if (!r.ok) throw new Error(r.message);
  return r.data.classes;
}

/**
 * Fetch direct neighbors for a node via /api/v1/vdb/edges. Imperative
 * helper rather than a hook — Viz expansion is event-driven and
 * appends to a Cytoscape instance, not a React render tree.
 */
export async function fetchVdbEdges(
  token: string | undefined,
  from: string,
  options: { include_meta?: boolean; include_types?: boolean; graph?: string | null } = {},
): Promise<VdbEdge[]> {
  const params = new URLSearchParams({ from });
  if (options.include_meta) params.set('include_meta', 'true');
  if (options.include_types) params.set('include_types', 'true');
  if (options.graph) params.set('graph', options.graph);

  const r = await apiFetch<EdgesResponse>(`/api/v1/vdb/edges?${params.toString()}`, token);
  if (!r.ok) throw new Error(r.message);
  return r.data.edges;
}

export interface VdbPathEdge {
  source: string;
  target: string;
  relationship: string;
  /** The relationship's display label (label, else the URI). */
  label: string;
  weight: number;
}

export interface VdbPath {
  found: boolean;
  nodes: string[];
  /** Display labels aligned with `nodes` (label, else the URI). */
  node_labels: string[];
  edges: VdbPathEdge[];
  total_length: number;
  confidence: number;
  path_type: string;
}

/** Shortest semantic path between two entity URIs (Viz "Find Path"). */
// ---- saved graph layouts ----
// Automatic placement is a starting point; the arrangement a user settles on by
// hand is worth keeping. Server-side per (user, graph) so it survives browsers
// and devices. Model coordinates only — the viewport transform is not saved, so
// a restored layout re-fits to whatever canvas is current.

export interface NodePosition {
  x: number;
  y: number;
}

export interface VizLayout {
  positions: Record<string, NodePosition>;
  updated_at?: string;
}

/** An empty `positions` map clears the saved layout (back to automatic).
 *  Returns the result — a discarded one is how hand-arranged layouts went
 *  missing silently. */
export async function saveVizLayout(
  token: string,
  graph: string | null,
  positions: Record<string, NodePosition>,
): Promise<ApiResult<{ cleared?: boolean; existed?: boolean }>> {
  return apiFetch('/api/v1/viz/layout', token, {
    method: 'PUT',
    body: JSON.stringify({ graph: graph ?? '', positions }),
  });
}

export async function fetchVdbPath(token: string | undefined, from: string, to: string): Promise<VdbPath> {
  const params = new URLSearchParams({ from, to });
  const r = await apiFetch<VdbPath>(`/api/v1/vdb/path?${params.toString()}`, token);
  if (!r.ok) throw new Error(r.message);
  return r.data;
}
